/**
 * Chat bubble for the AI Coach panel. Handles three cases:
 *   • system → centered pill
 *   • user   → right-aligned blue gradient (optionally with attached photo)
 *   • coach  → left-aligned card with bot avatar
 */
import { Bot } from 'lucide-react';

export const CoachMessageBubble = ({ message, userName }) => {
  const isUser = message.role === 'user';
  const isSystem = message.is_system;
  const hasImage = !!message.image_url;

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <span className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[11px]">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}>
      {!isUser && (
        <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-yellow-300 to-primary
          flex items-center justify-center text-black shadow-[0_0_8px_rgba(212,175,55,0.45)]">
          <Bot className="h-3.5 w-3.5" strokeWidth={2.3} />
        </div>
      )}
      <div className={`max-w-[78%] flex flex-col ${isUser ? 'items-end' : 'items-start'} gap-1.5`}>
        {hasImage && (
          <a
            href={message.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`block overflow-hidden rounded-2xl border ${isUser ? 'border-sky-300/40' : 'border-white/15'} shadow-[0_4px_14px_rgba(0,0,0,0.35)] hover:opacity-90 transition-opacity`}
            data-testid="ai-coach-message-image"
          >
            <img
              src={message.image_url}
              alt="Attached"
              className="max-h-52 w-auto object-cover"
            />
          </a>
        )}
        {message.content && (
          <div
            className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap
              ${isUser
                ? 'bg-gradient-to-br from-sky-500 to-blue-700 text-white rounded-br-md shadow-[0_4px_14px_rgba(56,189,248,0.35)]'
                : 'bg-white/[0.06] border border-white/10 text-white/90 rounded-bl-md'
              }`}
          >
            {message.content}
          </div>
        )}
      </div>
      {isUser && (
        <div className="h-7 w-7 shrink-0 rounded-full bg-white/10 border border-white/15
          flex items-center justify-center text-white/80 text-[11px] font-bold uppercase">
          {(userName || 'U').slice(0, 1)}
        </div>
      )}
    </div>
  );
};

export default CoachMessageBubble;
